import { Component } from '@theme/component';

/**
 * @typedef {object} Refs
 * @property {HTMLElement} wrapper – The wrapper container for announcement messages.
 * @property {HTMLElement[]} messages – The announcement message elements.
 */

/**
 * A custom element that manages an announcement bar with automatic scrolling between messages.
 *
 * @extends Component<Refs>
 */
class AnnouncementBar extends Component {
  currentIndex = 0;
  /** @type {number | null} */
  intervalId = null;
  transitionDuration = 500; // Durée de transition CSS en ms
  isPaused = false;
  isHoverPaused = false;
  isEditorPaused = false;
  slideDuration = 5000;
  isTransitioning = false;
  #pauseHandler = this.#pause.bind(this);
  #resumeHandler = this.#resume.bind(this);
  #blockSelectHandler = this.#handleBlockSelect.bind(this);
  #sectionSelectHandler = this.#handleSectionSelect.bind(this);
  #sectionDeselectHandler = this.#handleSectionDeselect.bind(this);

  connectedCallback() {
    super.connectedCallback();

    this.#setupThemeEditorListeners();

    const { wrapper, messages } = this.refs;
    if (!wrapper || !messages || messages.length <= 1) return;

    // Initialiser tous les messages sauf le premier en bas
    messages.forEach((message, index) => {
      if (index > 0) {
        message.style.transform = 'translateY(100%)';
        message.style.opacity = '0';
        message.style.pointerEvents = 'none';
      } else {
        // Le premier message est visible
        message.style.transform = 'translateY(0)';
        message.style.opacity = '1';
        message.style.pointerEvents = 'auto';
      }
    });

    // Lire la durée de transition depuis data-slide-duration (en secondes)
    const slideDuration = parseFloat(this.dataset.slideDuration || '5') || 5;
    this.slideDuration = slideDuration * 1000;
    const durationMs = this.slideDuration;

    // Ajouter les écouteurs d'événements pour la pause au survol
    this.addEventListener('mouseenter', this.#pauseHandler);
    this.addEventListener('mouseleave', this.#resumeHandler);

    // Démarrer le défilement automatique
    this.startAutoScroll(durationMs);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    // Nettoyer l'intervalle si le composant est retiré du DOM
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }

    // Retirer les écouteurs d'événements
    this.removeEventListener('mouseenter', this.#pauseHandler);
    this.removeEventListener('mouseleave', this.#resumeHandler);
    this.#removeThemeEditorListeners();
  }

  #pause() {
    this.isHoverPaused = true;
    this.#updateAutoScrollState();
  }

  #resume() {
    this.isHoverPaused = false;
    this.#updateAutoScrollState();
  }

  /**
   * Starts the automatic scrolling between messages.
   * @param {number} durationMs - The duration in milliseconds between each message transition.
   */
  startAutoScroll(durationMs) {
    const { messages } = this.refs;
    if (!messages || messages.length <= 1) return;

    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }

    // Fonction pour passer au message suivant
    const nextMessage = () => {
      const nextIndex = (this.currentIndex + 1) % messages.length;
      this.#transitionTo(nextIndex);
    };

    // Démarrer l'intervalle
    this.intervalId = setInterval(nextMessage, durationMs);
  }

  #setupThemeEditorListeners() {
    if (typeof Shopify === 'undefined' || !Shopify?.designMode) return;
    document.addEventListener('shopify:block:select', this.#blockSelectHandler);
    document.addEventListener('shopify:section:select', this.#sectionSelectHandler);
    document.addEventListener('shopify:section:deselect', this.#sectionDeselectHandler);
  }

  #removeThemeEditorListeners() {
    document.removeEventListener('shopify:block:select', this.#blockSelectHandler);
    document.removeEventListener('shopify:section:select', this.#sectionSelectHandler);
    document.removeEventListener('shopify:section:deselect', this.#sectionDeselectHandler);
  }

  /**
   * @param {Event} event
   */
  #handleBlockSelect(event) {
    const { messages } = this.refs;
    if (!messages || messages.length <= 1) return;

    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    if (!this.contains(target)) return;

    const selectedMessage = target.closest('.announcement-message');
    if (!(selectedMessage instanceof HTMLElement)) return;

    const index = messages.indexOf(selectedMessage);
    if (index < 0 || index === this.currentIndex) return;

    this.#transitionTo(index);
  }

  /**
   * @param {Event} event
   */
  #handleSectionSelect(event) {
    const customEvent = /** @type {CustomEvent} */ (event);
    const eventSectionId = customEvent.detail?.sectionId;

    if (eventSectionId && this.id && eventSectionId === this.id) {
      this.isEditorPaused = true;
      this.#updateAutoScrollState();
    }
  }

  /**
   * @param {Event} event
   */
  #handleSectionDeselect(event) {
    const customEvent = /** @type {CustomEvent} */ (event);
    const eventSectionId = customEvent.detail?.sectionId;

    if (eventSectionId && this.id && eventSectionId === this.id) {
      this.isEditorPaused = false;
      this.#updateAutoScrollState();
    }
  }

  #updateAutoScrollState() {
    const { messages } = this.refs;
    if (!messages || messages.length <= 1) return;

    const shouldPause = this.isHoverPaused || this.isEditorPaused;

    if (shouldPause) {
      if (this.intervalId) {
        clearInterval(this.intervalId);
        this.intervalId = null;
      }
      this.isPaused = true;
      return;
    }

    this.isPaused = false;
    if (!this.intervalId) {
      this.startAutoScroll(this.slideDuration);
    }
  }

  /**
   * @param {number} nextIndex
   */
  #transitionTo(nextIndex) {
    const { messages } = this.refs;
    if (!messages || messages.length <= 1) return;
    if (this.isTransitioning) return;

    const currentMessage = messages[this.currentIndex];
    const nextMessageElement = messages[nextIndex];
    if (!currentMessage || !nextMessageElement) return;

    this.isTransitioning = true;

    nextMessageElement.style.transition = 'none';
    nextMessageElement.style.transform = 'translateY(100%)';
    nextMessageElement.style.opacity = '0';
    nextMessageElement.style.pointerEvents = 'none';

    requestAnimationFrame(() => {
      nextMessageElement.style.transition = '';

      requestAnimationFrame(() => {
        currentMessage.style.transition = '';
        currentMessage.offsetHeight;

        currentMessage.style.transform = 'translateY(-100%)';
        currentMessage.style.opacity = '0';
        currentMessage.style.pointerEvents = 'none';

        nextMessageElement.offsetHeight;
        nextMessageElement.style.transform = 'translateY(0)';
        nextMessageElement.style.opacity = '1';
        nextMessageElement.style.pointerEvents = 'auto';

        setTimeout(() => {
          currentMessage.style.transition = 'none';
          currentMessage.style.transform = 'translateY(100%)';
          currentMessage.style.opacity = '0';
          currentMessage.style.pointerEvents = 'none';

          requestAnimationFrame(() => {
            currentMessage.style.transition = '';
          });

          this.currentIndex = nextIndex;
          this.isTransitioning = false;
        }, this.transitionDuration);
      });
    });
  }
}

customElements.define('announcement-bar', AnnouncementBar);
